import React from 'react';
import { sortMembersByDisplayNumber } from '../utils/classMemberNumbers';
import { useParams, useSearchParams } from 'react-router-dom';
import ProgramSlideCanvas, { cloneSlideLayout } from '../components/program/ProgramSlideCanvas';
import ProgramSlideTitlePanel from '../components/program/ProgramSlideTitlePanel';
import ProgramSlideCommunicationPanel from '../components/program/ProgramSlideCommunicationPanel';
import { normalizeWeeklyGuideColorHex } from '../constants/weeklyLearningGuide';
import {
  fetchPublicPresentationByToken,
  fetchPublicPresentationMetaByToken,
} from '../services/presentationPublicShareService';
import {
  fetchPublicCommunicationByToken,
  submitPublicCommunicationResponse,
  verifyPublicCommunicationEntry,
  type PresentationCommunicationMember,
  type PresentationCommunicationResponse,
} from '../services/presentationCommunicationService';
import { EMPTY_SLIDE_LAYOUT, type ProgramSlideLayoutV1 } from '../types/programContentSlideCanvas';
import type { ProgramContentSlidePayloadV1 } from '../utils/programContentSlideStorage';
import type { ClassMemberRow } from '../utils/classMembersStorage';
import type { CommunicationQuestion } from '../utils/presentationCommunicationAnswers';
import { lightenRgbHexTowardWhite } from '../utils/hexColorLighten';
import {
  footerSlideTabKey,
  isSlideFooterDotsScrollable,
  scrollFooterSlideTabIntoView,
  slideFooterDotsScrollStyle,
} from '../utils/slideFooterDotsNav';

const SLIDE_TRACK_BG_LIGHTEN = 0.8;

function membersFromPublicBoard(list: PresentationCommunicationMember[]): ClassMemberRow[] {
  return sortMembersByDisplayNumber(
    list.map((m) => ({
      id: m.id,
      name: m.name,
      gender: '' as const,
      groupNo: null,
      entryNumber: '',
      ...(m.seatPosition ? { seatPosition: m.seatPosition } : {}),
      ...(m.studentNumber != null ? { studentNumber: m.studentNumber } : {}),
    }))
  );
}

const PublicPresentationPage: React.FC = () => {
  const { token = '' } = useParams<{ token: string }>();
  const [searchParams] = useSearchParams();
  // "질문 QR": 슬라이드 없이 질문 페이지만 보여주는 전용 링크 (같은 공개 세션 토큰을 재사용)
  const questionOnly = searchParams.get('view') === 'question';
  const [loading, setLoading] = React.useState(true);
  const [loadError, setLoadError] = React.useState<string | null>(null);
  const [meta, setMeta] = React.useState<ProgramContentSlidePayloadV1['meta'] | null>(null);
  const [extraPageCount, setExtraPageCount] = React.useState(1);
  const [layouts, setLayouts] = React.useState<Record<number, ProgramSlideLayoutV1>>({});
  const [selectedPageIndex, setSelectedPageIndex] = React.useState<number | null>(null);
  const [isCommunicationView, setIsCommunicationView] = React.useState(false);
  const [commPrompt, setCommPrompt] = React.useState('');
  const [commQuestion, setCommQuestion] = React.useState<CommunicationQuestion | null>(null);
  const [commQuestionId, setCommQuestionId] = React.useState<string | null>(null);
  const [commIsOpen, setCommIsOpen] = React.useState(false);
  const [commMultiResponse, setCommMultiResponse] = React.useState(false);
  const [commMembers, setCommMembers] = React.useState<ClassMemberRow[]>([]);
  const [commResponses, setCommResponses] = React.useState<PresentationCommunicationResponse[]>([]);
  const [memberName, setMemberName] = React.useState('');
  const [entryNumber, setEntryNumber] = React.useState('');
  const [draftBody, setDraftBody] = React.useState('');
  const [submitting, setSubmitting] = React.useState(false);
  const [submitMessage, setSubmitMessage] = React.useState('');
  const [submitError, setSubmitError] = React.useState('');
  const footerDotsScrollRef = React.useRef<HTMLDivElement>(null);
  const bodyScrollRef = React.useRef<HTMLDivElement>(null);

  const refreshPublicCommunication = React.useCallback(async () => {
    const trimmed = token.trim();
    if (!trimmed) return;
    const board = await fetchPublicCommunicationByToken(trimmed);
    if (!board) {
      setCommPrompt('');
      setCommQuestion(null);
      setCommQuestionId(null);
      setCommIsOpen(false);
      setCommMultiResponse(false);
      setCommMembers([]);
      setCommResponses([]);
      return;
    }
    const nextQuestionId = board.questionId;
    const nextQuestion = board.question;
    setCommPrompt(board.promptText);
    setCommQuestion(nextQuestion);
    setCommQuestionId(nextQuestionId);
    setCommIsOpen(board.isOpen);
    setCommMultiResponse(Boolean(board.multiResponse));
    setCommMembers(membersFromPublicBoard(board.members));
  }, [token]);

  /** 공개 보드는 다른 학생 답변을 주지 않으므로, 본인 확인 RPC 로 내 답변만 받아 제출 한도를 판단한다 */
  const enteredRef = React.useRef(false);
  const loadMyResponses = React.useCallback(async () => {
    const trimmed = token.trim();
    if (!trimmed) return;
    const res = await verifyPublicCommunicationEntry({ token: trimmed, memberName, entryNumber });
    enteredRef.current = true;
    setCommResponses(res.myResponses);
  }, [token, memberName, entryNumber]);

  React.useEffect(() => {
    if (!enteredRef.current) return;
    setCommResponses([]);
    void loadMyResponses().catch(() => undefined);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- 문항이 바뀔 때만 내 답변을 다시 받는다
  }, [commQuestionId]);

  // 질문이 바뀌면(또는 유형이 바뀌면) 이전 답안 초안을 비워 응답 양식을 다시 맞춤
  const lastCommQuestionKeyRef = React.useRef<string>('');
  React.useEffect(() => {
    const key = `${commQuestionId ?? ''}|${commQuestion?.type ?? ''}`;
    if (lastCommQuestionKeyRef.current && lastCommQuestionKeyRef.current !== key) {
      setDraftBody('');
      setSubmitMessage('');
      setSubmitError('');
    }
    lastCommQuestionKeyRef.current = key;
  }, [commQuestionId, commQuestion?.type]);

  React.useEffect(() => {
    const trimmed = token.trim();
    if (!trimmed) {
      setLoading(false);
      setLoadError('유효하지 않은 공개보기 링크입니다.');
      return;
    }
    let cancelled = false;
    setLoading(true);
    setLoadError(null);
    void (async () => {
      try {
        // 질문 QR은 슬라이드를 전혀 표시하지 않으므로 무거운 layout_json은 아예 조회하지 않는다.
        if (questionOnly) {
          const metaPayload = await fetchPublicPresentationMetaByToken(trimmed);
          if (cancelled) return;
          if (!metaPayload) {
            setLoadError('공개보기 링크가 만료되었거나 존재하지 않습니다.');
            return;
          }
          setMeta(metaPayload.meta);
          setExtraPageCount(0);
          setLayouts({});
        } else {
          const fullPayload = await fetchPublicPresentationByToken(trimmed);
          if (cancelled) return;
          if (!fullPayload) {
            setLoadError('공개보기 링크가 만료되었거나 존재하지 않습니다.');
            return;
          }
          setMeta(fullPayload.meta);
          setExtraPageCount(fullPayload.extraPageCount);
          setLayouts(fullPayload.layouts);
        }
        setSelectedPageIndex(null);
        setIsCommunicationView(questionOnly);
        try {
          await refreshPublicCommunication();
        } catch {
          /* 의사소통 미설정·마이그레이션 전에도 슬라이드는 표시 */
        }
      } catch (e) {
        if (!cancelled) setLoadError(e instanceof Error ? e.message : '불러오기에 실패했습니다.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token, refreshPublicCommunication, questionOnly]);

  React.useEffect(() => {
    if (!isCommunicationView) return;
    const id = window.setInterval(() => {
      void refreshPublicCommunication().catch(() => undefined);
    }, 4000);
    return () => window.clearInterval(id);
  }, [isCommunicationView, refreshPublicCommunication]);

  const totalSlides = 1 + extraPageCount;
  const slideIndex = isCommunicationView
    ? totalSlides
    : selectedPageIndex == null
      ? 0
      : Math.min(extraPageCount, selectedPageIndex + 1);
  const pageIndexForCanvas = selectedPageIndex ?? -1;
  const layoutForCanvas = React.useMemo(
    () => cloneSlideLayout(layouts[pageIndexForCanvas] ?? EMPTY_SLIDE_LAYOUT),
    [layouts, pageIndexForCanvas]
  );

  const openSlidePage = React.useCallback((pageIndex: number | null) => {
    setIsCommunicationView(false);
    setSelectedPageIndex(pageIndex);
  }, []);

  const goPrev = React.useCallback(() => {
    if (isCommunicationView) {
      setIsCommunicationView(false);
      setSelectedPageIndex(extraPageCount > 0 ? extraPageCount - 1 : null);
      return;
    }
    setSelectedPageIndex((current) => {
      if (current == null) return null;
      if (current <= 0) return null;
      return current - 1;
    });
  }, [extraPageCount, isCommunicationView]);

  const goNext = React.useCallback(() => {
    if (isCommunicationView) return;
    if (selectedPageIndex == null) {
      if (extraPageCount > 0) setSelectedPageIndex(0);
      else setIsCommunicationView(true);
      return;
    }
    if (selectedPageIndex >= extraPageCount - 1) {
      setIsCommunicationView(true);
      return;
    }
    setSelectedPageIndex(selectedPageIndex + 1);
  }, [extraPageCount, isCommunicationView, selectedPageIndex]);

  React.useEffect(() => {
    // 질문 전용 링크는 슬라이드로 이동할 수 없어야 하므로 페이지 전환 키를 아예 걸지 않는다.
    if (questionOnly) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.ctrlKey || e.altKey || e.metaKey) return;
      const tag = (e.target as HTMLElement | null)?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return;
      if (e.key === 'ArrowLeft' || e.key === 'PageUp') {
        e.preventDefault();
        goPrev();
      } else if (e.key === 'ArrowRight' || e.key === 'PageDown') {
        e.preventDefault();
        goNext();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [goNext, goPrev, questionOnly]);

  React.useEffect(() => {
    scrollFooterSlideTabIntoView(
      footerDotsScrollRef.current,
      isCommunicationView ? 'communication' : footerSlideTabKey(selectedPageIndex)
    );
  }, [selectedPageIndex, extraPageCount, isCommunicationView]);

  // iOS: 슬라이드↔타이틀 전환 시 가로 모멘텀/잔여 scrollLeft가 남아 제스처가 안 먹는 경우 리셋
  React.useEffect(() => {
    const el = bodyScrollRef.current;
    if (!el) return;
    el.scrollLeft = 0;
  }, [selectedPageIndex, isCommunicationView]);

  const handleEnterRequest = React.useCallback(async () => {
    if (!token.trim()) throw new Error('유효하지 않은 공개보기 링크입니다.');
    await loadMyResponses();
  }, [token, loadMyResponses]);

  const handleSubmitResponse = React.useCallback(async () => {
    const trimmed = token.trim();
    if (!trimmed) return;
    setSubmitting(true);
    setSubmitError('');
    setSubmitMessage('');
    try {
      await submitPublicCommunicationResponse({
        token: trimmed,
        memberName,
        entryNumber,
        bodyText: draftBody,
      });
      setDraftBody('');
      await refreshPublicCommunication();
      await loadMyResponses().catch(() => undefined);
      // 단일·복수 모두 제출 직후 버튼 아래에 완료 안내를 남긴다(복수는 한도 도달 시 패널 잠금 문구로 대체).
      setSubmitMessage('응답이 제출 완료되었습니다.');
      if (commMultiResponse) {
        window.setTimeout(() => setSubmitMessage(''), 3200);
      }
    } catch (e) {
      setSubmitError(e instanceof Error ? e.message : '제출에 실패했습니다.');
    } finally {
      setSubmitting(false);
    }
  }, [
    draftBody,
    memberName,
    entryNumber,
    refreshPublicCommunication,
    loadMyResponses,
    token,
    commMultiResponse,
  ]);

  if (loading) {
    return (
      <div className="cp-program-slide-root cp-public-presentation-root" role="status">
        <header className="cp-topnav cp-program-slide-topnav" role="banner">
          <div className="cp-topnav-left">
            <span className="cp-program-slide-topnav-title">{questionOnly ? '질문 응답' : '공개보기 슬라이드'}</span>
          </div>
        </header>
        <p className="cp-program-slide-error">불러오는 중입니다…</p>
      </div>
    );
  }

  if (loadError || !meta) {
    return (
      <div className="cp-program-slide-root cp-public-presentation-root" role="alert">
        <header className="cp-topnav cp-program-slide-topnav" role="banner">
          <div className="cp-topnav-left">
            <span className="cp-program-slide-topnav-title">{questionOnly ? '질문 응답' : '공개보기 슬라이드'}</span>
          </div>
        </header>
        <p className="cp-program-slide-error">{loadError ?? '표시할 슬라이드가 없습니다.'}</p>
      </div>
    );
  }

  const track = (meta.templateLabel ?? '').trim();
  const unit = (meta.unit ?? '').trim();
  const program = (meta.programContent ?? '').trim();
  const pageMain = (meta.page ?? '').trim();
  const pageAssist = (meta.assistantPage ?? '').trim();
  const weeklyTrackHex = normalizeWeeklyGuideColorHex(meta.weeklyGuideTrackColorHex ?? '');
  const trackSwatchBg = weeklyTrackHex ? lightenRgbHexTowardWhite(weeklyTrackHex, SLIDE_TRACK_BG_LIGHTEN) : '';
  const trackSwatchStyle: React.CSSProperties | undefined = weeklyTrackHex
    ? {
        backgroundColor: trackSwatchBg,
        color: '#141414',
        borderColor: 'rgba(0, 0, 0, 0.22)',
      }
    : undefined;
  const isFirst = !isCommunicationView && slideIndex === 0;
  const titlePanelProps = {
    learningTitle: (meta.programContent ?? '').trim(),
    achievementText: meta.achievementText ?? '',
    learningMemoNote: (meta.learningMemoNote ?? '').trim(),
    evaluationMemoNote: (meta.evaluationMemoNote ?? '').trim(),
  };

  const footerPageTabCount = extraPageCount;
  const footerDotsScrollable = isSlideFooterDotsScrollable(footerPageTabCount);
  const footerDotsNavStyle = slideFooterDotsScrollStyle(footerPageTabCount, true, false, true);

  const dots =
    totalSlides > 0 ? (
      <div
        ref={footerDotsScrollRef}
        className={
          'cp-program-slide-dots-scroll' + (footerDotsScrollable ? ' is-scrollable has-title' : ' has-title')
        }
        style={footerDotsNavStyle}
      >
        <div className="cp-program-slide-dots" role="tablist" aria-label="슬라이드 페이지">
          <button
            type="button"
            role="tab"
            aria-selected={!isCommunicationView && slideIndex === 0}
            className={
              'cp-program-slide-dot is-title' +
              (!isCommunicationView && slideIndex === 0 ? ' is-active' : '')
            }
            onClick={() => openSlidePage(null)}
            title="타이틀 슬라이드"
            data-footer-slide-tab="title"
          >
            <span className="cp-program-slide-dot-label">타이틀</span>
          </button>
          {Array.from({ length: extraPageCount }, (_, pageIndex) => (
            <button
              key={pageIndex}
              type="button"
              role="tab"
              aria-selected={!isCommunicationView && selectedPageIndex === pageIndex}
              className={
                'cp-program-slide-dot' +
                (!isCommunicationView && selectedPageIndex === pageIndex ? ' is-active' : '')
              }
              onClick={() => openSlidePage(pageIndex)}
              title={`슬라이드 ${pageIndex + 1}`}
              data-footer-slide-tab={footerSlideTabKey(pageIndex)}
            >
              <span className="cp-program-slide-dot-label">{String(pageIndex + 1)}</span>
            </button>
          ))}
          <button
            type="button"
            role="tab"
            aria-selected={isCommunicationView}
            className={'cp-program-slide-dot is-communication' + (isCommunicationView ? ' is-active' : '')}
            onClick={() => setIsCommunicationView(true)}
            title="질문"
            data-footer-slide-tab="communication"
          >
            <span className="cp-program-slide-dot-label">질문</span>
          </button>
        </div>
      </div>
    ) : null;

  return (
    <div className="cp-program-slide-root cp-public-presentation-root">
      <header className="cp-topnav cp-program-slide-topnav" role="banner">
        <div className="cp-topnav-left">
          <span className="cp-program-slide-topnav-title">{questionOnly ? '질문 응답' : '공개보기 슬라이드'}</span>
          <span className="cp-program-slide-mode-toggle is-view" aria-hidden>
            보기모드
          </span>
        </div>
      </header>

      <div className="cp-program-slide-hero cp-program-slide-hero--headline">
        <div className="cp-program-slide-hero-inner">
          <div className="cp-program-slide-hero-body">
            <div className="cp-program-slide-hero-meta-row" role="group" aria-label="편제, 단원, 쪽수">
              {track ? (
                <span
                  className={
                    'cp-program-slide-hero-track' +
                    (weeklyTrackHex ? ' cp-program-slide-hero-track--weekly-swatch' : '')
                  }
                  style={trackSwatchStyle}
                >
                  {track}
                </span>
              ) : null}
              <div className="cp-program-slide-hero-unit">{unit || '—'}</div>
              {pageMain || pageAssist ? (
                <div className="cp-program-slide-hero-pages" aria-label="쪽수 및 보조쪽수">
                  {pageMain ? <span className="cp-program-slide-hero-page-main">{pageMain}</span> : null}
                  {pageMain && pageAssist ? <span className="cp-program-slide-hero-page-gap"> </span> : null}
                  {pageAssist ? <span className="cp-program-slide-hero-page-assist">{pageAssist}</span> : null}
                </div>
              ) : null}
            </div>
            <div className="cp-program-slide-hero-program">{program || '—'}</div>
          </div>
        </div>
      </div>

      <div className="cp-program-slide-frame">
        <div className="cp-program-slide-body-scroll" ref={bodyScrollRef}>
          <div className="cp-program-slide-body-stage">
            {isCommunicationView ? (
              <div className="cp-program-slide-communication-host">
                <ProgramSlideCommunicationPanel
                  mode="student"
                  promptText={commPrompt}
                  question={commQuestion}
                  isOpen={commIsOpen}
                  multiResponse={commMultiResponse}
                  members={commMembers}
                  responses={commResponses}
                  memberName={memberName}
                  onMemberNameChange={setMemberName}
                  entryNumber={entryNumber}
                  onEntryNumberChange={setEntryNumber}
                  draftBody={draftBody}
                  onDraftBodyChange={setDraftBody}
                  onSubmitResponse={() => void handleSubmitResponse()}
                  onEnterRequest={handleEnterRequest}
                  submitting={submitting}
                  submitMessage={submitMessage}
                  submitError={submitError}
                />
              </div>
            ) : isFirst ? (
              <ProgramSlideTitlePanel {...titlePanelProps} styleStorageKey={token.trim() || undefined} />
            ) : (
              <ProgramSlideCanvas layout={layoutForCanvas} readOnly viewMode onLayoutChange={() => {}} />
            )}
          </div>
        </div>

        <div className="cp-program-slide-rule cp-program-slide-rule--fullbleed" aria-hidden />

        {questionOnly ? null : (
          <div className="cp-program-slide-footer-nav">
            <button
              type="button"
              className="cp-program-slide-nav-btn"
              onClick={goPrev}
              disabled={!isCommunicationView && slideIndex <= 0}
              aria-label="이전 슬라이드"
            >
              &lt;
            </button>
            <div className="cp-program-slide-footer-nav-center">{dots}</div>
            <button
              type="button"
              className="cp-program-slide-nav-btn"
              onClick={goNext}
              disabled={isCommunicationView}
              aria-label="다음 슬라이드"
            >
              &gt;
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default PublicPresentationPage;
